import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Car,
  ChevronDown,
  Clock,
  PartyPopper,
  Package,
  RefreshCw,
  Sparkles,
  Ticket,
  UserPlus,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { FirstRunCard } from '@/components/FirstRunCard';
import { PassCreateDialog, type PassCreateType } from '@/components/resident/PassCreateDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { QueryStateBoundary } from '@/components/ui/query-state-boundary';
import { Skeleton } from '@/components/ui/skeleton';
import { INVITATION_TYPE_LABEL } from '@/constants/labels';
import { POLLING_INTERVALS } from '@/constants/polling';
import { useAuth } from '@/contexts/AuthContext';
import { useResidentProperty } from '@/hooks/useResidentProperty';
import { useTranslation } from '@/hooks/useTranslation';
import { useVisibilityPolling } from '@/hooks/useVisibilityPolling';
import { formatDateTime, statusBadgeClass } from '@/lib/utils';
import {
  approveWalkIn,
  cancelDeliveryPass,
  cancelTaxiPass,
  denyWalkIn,
  getDeliveryPasses,
  getDomesticWorkers,
  getInvitations,
  getRecurringPasses,
  getResidentPendingWalkIns,
  getTaxiPasses,
} from '@/services/api.client';
import type { DeliveryPass, Invitation, RecurringPass, TaxiPass, WalkInRequest } from '@/types';

// ─── Walk-In Banner ───────────────────────────────────────────────────────────

const WALK_INS_PENDING_KEY = ['walk-ins', 'pending'];

function WalkInBanner({ walkIn }: { walkIn: WalkInRequest }) {
  const qc = useQueryClient();

  const removeFromCache = () => {
    qc.setQueryData<WalkInRequest[]>(WALK_INS_PENDING_KEY, (old) =>
      old ? old.filter((w) => w.id !== walkIn.id) : old,
    );
  };

  const approve = useMutation({
    mutationFn: () => approveWalkIn(walkIn.id),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: WALK_INS_PENDING_KEY });
      const previous = qc.getQueryData<WalkInRequest[]>(WALK_INS_PENDING_KEY);
      removeFromCache();
      return { previous };
    },
    onError: (_err, _args, context) => {
      if (context?.previous) {
        qc.setQueryData(WALK_INS_PENDING_KEY, context.previous);
      }
      toast.error('Action failed. Please try again.');
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: WALK_INS_PENDING_KEY });
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
  const deny = useMutation({
    mutationFn: () => denyWalkIn(walkIn.id),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: WALK_INS_PENDING_KEY });
      const previous = qc.getQueryData<WalkInRequest[]>(WALK_INS_PENDING_KEY);
      removeFromCache();
      return { previous };
    },
    onError: (_err, _args, context) => {
      if (context?.previous) {
        qc.setQueryData(WALK_INS_PENDING_KEY, context.previous);
      }
      toast.error('Action failed. Please try again.');
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: WALK_INS_PENDING_KEY });
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
  const busy = approve.isPending || deny.isPending;

  return (
    <div
      className="flex items-center justify-between gap-4 rounded-xl border px-4 py-3"
      style={{ borderColor: 'var(--status-amber)', background: 'var(--status-amber-tint)' }}
    >
      <div className="min-w-0">
        <p className="text-sm font-semibold" style={{ color: 'var(--status-amber-text)' }}>
          Walk-in request pending
        </p>
        <p className="text-xs text-[var(--text-secondary)] truncate">
          {walkIn.visitorName} is at the gate requesting entry.
        </p>
      </div>
      <div className="flex gap-2 shrink-0">
        <Button
          size="sm"
          disabled={busy}
          onClick={() => approve.mutate()}
          style={{ background: 'var(--status-green)', color: 'var(--text-inverse)' }}
        >
          Allow
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => deny.mutate()}
          style={{ borderColor: 'var(--status-red)', color: 'var(--status-red-text)' }}
        >
          Deny
        </Button>
      </div>
    </div>
  );
}

// ─── Invitation Row ───────────────────────────────────────────────────────────

function InvitationRow({ inv }: { inv: Invitation }) {
  const typeLabel = INVITATION_TYPE_LABEL[inv.invitationType] ?? 'Visitor';
  const isPendingReg = inv.status === 'awaiting_visitor_registration';
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-[var(--bg-surface-raised)] last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[var(--text-primary)] truncate">{inv.visitorName}</p>
        <p className="text-xs text-[var(--text-tertiary)]">{formatDateTime(inv.validFrom)}</p>
      </div>
      {isPendingReg ? (
        <span className="rounded-full bg-[var(--status-amber-tint)] border border-[var(--status-amber)] px-2.5 py-0.5 text-xs font-medium text-[var(--status-amber-text)]">
          Awaiting registration
        </span>
      ) : (
        <span className="rounded-full border border-[var(--bg-surface-raised)] px-2.5 py-0.5 text-xs font-medium text-[var(--text-secondary)]">
          {typeLabel}
        </span>
      )}
    </div>
  );
}

// ─── Recurring Row ────────────────────────────────────────────────────────────

function RecurringRow({ pass }: { pass: RecurringPass }) {
  const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const days = pass.scheduleDays?.map((d) => DAY_ABBR[d]).join(', ') ?? '—';
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-[var(--bg-surface-raised)] last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[var(--text-primary)] truncate">
          {pass.visitorName}
        </p>
        <p className="text-xs text-[var(--text-tertiary)]">
          {days} · {pass.entryTimeStart}–{pass.entryTimeEnd}
        </p>
      </div>
      <Badge className={statusBadgeClass(pass.status)}>{pass.status.toUpperCase()}</Badge>
    </div>
  );
}

// ─── New Invitation Panel — single discoverable entry for all invitation types ──

type InviteType =
  | {
      kind: 'link';
      to: string;
      icon: typeof UserPlus;
      title: string;
      description: string;
      primary: boolean;
    }
  | {
      kind: 'dialog';
      dialogType: PassCreateType;
      icon: typeof UserPlus;
      title: string;
      description: string;
      primary: boolean;
    };

const INVITE_TYPES: readonly InviteType[] = [
  {
    kind: 'link',
    to: '/resident/invite',
    icon: UserPlus,
    title: 'Coming once',
    description: 'A guest or a one-off worker — friend, family, plumber or fundi',
    primary: true,
  },
  {
    kind: 'link',
    to: '/resident/passes?tab=household&create=1',
    icon: RefreshCw,
    title: 'Regular visitor',
    description: 'Someone who comes again and again — gardener or a day-to-day contractor',
    primary: false,
  },
  {
    kind: 'link',
    to: '/resident/passes?tab=domestic&create=1',
    icon: Users,
    title: 'House help',
    description: 'Cleaner, nanny or helper on a weekly schedule',
    primary: false,
  },
  {
    kind: 'dialog',
    dialogType: 'quick_code',
    icon: Ticket,
    title: 'Quick code',
    description: 'A one-time code for a delivery or taxi — share on WhatsApp',
    primary: false,
  },
  {
    kind: 'link',
    to: '/resident/events',
    icon: PartyPopper,
    title: 'Get-together',
    description: 'Inviting a group? Share a guest list or signup link',
    primary: false,
  },
] as const;

const itemClass = (primary: boolean) =>
  [
    'group flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors',
    primary
      ? 'border-transparent text-[var(--text-inverse)]'
      : 'border-[var(--bg-surface-raised)] text-[var(--text-secondary)] hover:bg-[var(--bg-surface-raised)] hover:text-[var(--text-primary)]',
  ].join(' ');

const iconWrapClass = (primary: boolean) =>
  [
    'flex size-8 shrink-0 items-center justify-center rounded-md',
    primary
      ? 'bg-[var(--text-primary)]/20'
      : 'bg-[var(--bg-surface-raised)] group-hover:bg-[var(--bg-surface)]',
  ].join(' ');

function InviteCardContents({ opt }: { opt: InviteType }) {
  return (
    <>
      <div className={iconWrapClass(opt.primary)}>
        <opt.icon className="size-4" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold truncate">{opt.title}</p>
        <p
          className={[
            'text-xs mt-0.5',
            opt.primary ? 'text-[var(--text-primary)]/80' : 'text-[var(--text-tertiary)]',
          ].join(' ')}
        >
          {opt.description}
        </p>
      </div>
    </>
  );
}

function NewInvitationPanel({ onOpenDialog }: { onOpenDialog: (type: PassCreateType) => void }) {
  const [showMore, setShowMore] = useState(false);
  const primary = INVITE_TYPES.find((t) => t.primary);
  const rest = INVITE_TYPES.filter((t) => !t.primary);

  const renderOption = (opt: InviteType) => {
    const style = opt.primary ? { background: 'var(--cta-primary-bg)' } : {};
    if (opt.kind === 'link') {
      return (
        <Link key={opt.title} to={opt.to} className={itemClass(opt.primary)} style={style}>
          <InviteCardContents opt={opt} />
        </Link>
      );
    }
    return (
      <button
        key={opt.title}
        type="button"
        onClick={() => onOpenDialog(opt.dialogType)}
        className={itemClass(opt.primary)}
        style={style}
      >
        <InviteCardContents opt={opt} />
      </button>
    );
  };

  return (
    <div className="rounded-xl border border-[var(--bg-surface-raised)] bg-[var(--bg-surface)] overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--bg-surface-raised)]">
        <Sparkles className="size-4 text-[var(--text-accent)]" />
        <p className="text-sm font-semibold text-[var(--text-primary)]">Create new invitation</p>
      </div>
      <div className="p-3 space-y-2">
        {/* Everyday action stays prominent; rarer types are one tap away. */}
        {primary && renderOption(primary)}

        {showMore && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">{rest.map(renderOption)}</div>
        )}

        <button
          type="button"
          onClick={() => setShowMore((v) => !v)}
          aria-expanded={showMore}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
        >
          {showMore ? 'Show fewer options' : 'More ways to invite'}
          <ChevronDown
            className={['size-4 transition-transform', showMore ? 'rotate-180' : ''].join(' ')}
          />
        </button>
      </div>
    </div>
  );
}

// ─── Active Codes Panel — live taxi + delivery codes the resident has issued ─

function timeRemaining(validUntil: string): string {
  const ms = new Date(validUntil).getTime() - Date.now();
  if (ms <= 0) return 'Expired';
  const totalMin = Math.floor(ms / 60_000);
  if (totalMin < 60) return `${totalMin} min remaining`;
  const hours = Math.floor(totalMin / 60);
  const minutes = totalMin % 60;
  if (minutes === 0) return `${hours}h remaining`;
  return `${hours}h ${minutes}m remaining`;
}

function ActiveCodeRow({
  type,
  validUntil,
  status,
  onCancel,
  busy,
}: {
  type: 'taxi' | 'delivery';
  validUntil: string;
  status: string;
  onCancel: () => void;
  busy: boolean;
}) {
  const Icon = type === 'taxi' ? Car : Package;
  const label = type === 'taxi' ? 'Taxi' : 'Delivery';
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-[var(--bg-surface-raised)] last:border-0">
      <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-[var(--bg-surface-raised)]">
        <Icon className="size-4 text-[var(--text-secondary)]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[var(--text-primary)]">{label}</p>
        <p className="text-xs text-[var(--text-tertiary)] flex items-center gap-1">
          <Clock className="size-3" />
          {timeRemaining(validUntil)}
        </p>
      </div>
      <Badge className={statusBadgeClass(status)}>{status.toUpperCase()}</Badge>
      <Button size="sm" variant="outline" onClick={onCancel} disabled={busy}>
        Cancel
      </Button>
    </div>
  );
}

function ActiveCodesPanel({ propertyId }: { propertyId: string }) {
  const qc = useQueryClient();
  const taxiQuery = useQuery({
    queryKey: ['taxi-passes', propertyId],
    queryFn: () => getTaxiPasses(propertyId),
  });
  const deliveryQuery = useQuery({
    queryKey: ['delivery-passes', propertyId],
    queryFn: () => getDeliveryPasses(propertyId),
  });

  const cancelTaxi = useMutation({
    mutationFn: (id: string) => cancelTaxiPass(propertyId, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['taxi-passes', propertyId] });
      toast.success('Taxi code cancelled');
    },
    onError: () => toast.error('Failed to cancel taxi code'),
  });

  const cancelDelivery = useMutation({
    mutationFn: (id: string) => cancelDeliveryPass(propertyId, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['delivery-passes', propertyId] });
      toast.success('Delivery code cancelled');
    },
    onError: () => toast.error('Failed to cancel delivery code'),
  });

  const now = Date.now();
  const isLive = (status: string, validUntil: string) =>
    status === 'active' && new Date(validUntil).getTime() > now;

  const liveTaxis: TaxiPass[] = (taxiQuery.data ?? []).filter((p) =>
    isLive(p.status, p.validUntil),
  );
  const liveDeliveries: DeliveryPass[] = (deliveryQuery.data ?? []).filter((p) =>
    isLive(p.status, p.validUntil),
  );
  const totalLive = liveTaxis.length + liveDeliveries.length;
  const isLoading = taxiQuery.isLoading || deliveryQuery.isLoading;

  // Taxi and delivery codes are short-lived (typically under an hour), so this
  // panel is empty most of the time. Hide it entirely when there is nothing
  // live rather than showing an empty card that just adds clutter to the home.
  if (!isLoading && totalLive === 0) {
    return null;
  }

  return (
    <div className="rounded-xl border border-[var(--bg-surface-raised)] bg-[var(--bg-surface)] overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--bg-surface-raised)]">
        <p className="text-sm font-semibold text-[var(--text-primary)]">Active codes</p>
        {totalLive > 0 && (
          <span className="text-xs text-[var(--text-tertiary)]">{totalLive} active</span>
        )}
      </div>
      <div className="px-4 py-1">
        {isLoading ? (
          <div className="space-y-3 py-2">
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <>
            {liveDeliveries.map((p) => (
              <ActiveCodeRow
                key={`d-${p.id}`}
                type="delivery"
                validUntil={p.validUntil}
                status={p.status}
                onCancel={() => cancelDelivery.mutate(p.id)}
                busy={cancelDelivery.isPending}
              />
            ))}
            {liveTaxis.map((p) => (
              <ActiveCodeRow
                key={`t-${p.id}`}
                type="taxi"
                validUntil={p.validUntil}
                status={p.status}
                onCancel={() => cancelTaxi.mutate(p.id)}
                busy={cancelTaxi.isPending}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Home Page ────────────────────────────────────────────────────────────────

export function HomePage() {
  const prop = useResidentProperty();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [dialogType, setDialogType] = useState<PassCreateType | null>(null);

  const invitationsQuery = useQuery({
    queryKey: [
      'invitations',
      prop?.propertyId,
      { statusIn: 'active,awaiting_visitor_registration', perPage: 5 },
    ],
    queryFn: () =>
      prop
        ? getInvitations(prop.propertyId, {
            statusIn: 'active,awaiting_visitor_registration',
            perPage: 5,
          })
        : null,
    enabled: !!prop,
  });

  const recurringQuery = useQuery({
    queryKey: ['recurring-passes', prop?.propertyId],
    queryFn: () => (prop ? getRecurringPasses(prop.propertyId) : null),
    enabled: !!prop,
  });

  const { data: domesticWorkers } = useQuery({
    queryKey: ['domestic-workers', prop?.propertyId],
    queryFn: () => (prop ? getDomesticWorkers(prop.propertyId) : null),
    enabled: !!prop,
  });

  const walkInPendingInterval = useVisibilityPolling(POLLING_INTERVALS.RESIDENT.WALK_IN_PENDING);

  const { data: pendingWalkInsData } = useQuery({
    queryKey: ['walk-ins', 'pending'],
    queryFn: getResidentPendingWalkIns,
    refetchInterval: walkInPendingInterval,
  });

  const pendingWalkIns = pendingWalkInsData ?? [];
  const anyError = invitationsQuery.isError || recurringQuery.isError;

  return (
    <div className="space-y-5">
      {user && (
        <FirstRunCard
          storageKey={`sl.firstrun.resident:${user.id}`}
          title={t('resident.firstRun.title')}
          description={t('resident.firstRun.description')}
          actions={[
            { label: t('resident.firstRun.inviteVisitor'), to: '/resident/invite' },
            { label: t('resident.firstRun.managePasses'), to: '/resident/passes' },
          ]}
        />
      )}

      {anyError && (
        <div
          role="alert"
          className="rounded-xl border border-[var(--border-error)] bg-[var(--status-red-tint)] p-4"
        >
          <p className="text-sm font-medium text-[var(--text-primary)]">
            Some data couldn&apos;t be loaded
          </p>
          <p className="text-sm text-[var(--text-secondary)]">
            Parts of your dashboard may show incomplete information. Try refreshing.
          </p>
        </div>
      )}

      {/* Walk-in banners — cap at 3 to avoid dominating the page */}
      {pendingWalkIns.slice(0, 3).map((w) => (
        <WalkInBanner key={w.id} walkIn={w} />
      ))}
      {pendingWalkIns.length > 3 && (
        <Link
          to="/resident/notifications"
          className="block rounded-xl border border-[var(--status-amber)] bg-[var(--status-amber-tint)] px-4 py-2.5 text-sm font-medium text-[var(--status-amber-text)] hover:opacity-90 transition-opacity text-center"
        >
          {pendingWalkIns.length - 3} more walk-in
          {pendingWalkIns.length - 3 === 1 ? '' : 's'} pending — view all →
        </Link>
      )}

      {/* Unified "Create new" panel — single discoverable entry for all invitation types */}
      <NewInvitationPanel
        onOpenDialog={(t) => {
          if (prop) setDialogType(t);
        }}
      />

      {/* Two-panel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-[var(--bg-surface-raised)] bg-[var(--bg-surface)] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--bg-surface-raised)]">
            <p className="text-sm font-semibold text-[var(--text-primary)]">Active Invitations</p>
            <Link
              to="/resident/visitors"
              className="text-xs text-[var(--text-accent)] hover:underline"
            >
              View all →
            </Link>
          </div>
          <div className="px-4 py-1">
            <QueryStateBoundary
              query={invitationsQuery}
              isEmpty={(d) => (d?.data ?? []).length === 0}
              errorTitle="Couldn't load invitations"
              emptyMessage={t('resident.home.invitationsEmpty')}
              loadingFallback={
                <div className="space-y-3 py-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="flex-1 space-y-1.5">
                        <Skeleton data-testid="skeleton" className="h-3.5 w-36" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </div>
                  ))}
                </div>
              }
            >
              {(res) => (
                <>
                  {res.data.slice(0, 5).map((inv) => (
                    <InvitationRow key={inv.id} inv={inv} />
                  ))}
                </>
              )}
            </QueryStateBoundary>
          </div>
        </div>

        <div className="rounded-xl border border-[var(--bg-surface-raised)] bg-[var(--bg-surface)] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--bg-surface-raised)]">
            <p className="text-sm font-semibold text-[var(--text-primary)]">Recurring Passes</p>
            <Link
              to="/resident/passes"
              className="text-xs text-[var(--text-accent)] hover:underline"
            >
              View all →
            </Link>
          </div>
          <div className="px-4 py-1">
            <QueryStateBoundary
              query={recurringQuery}
              isEmpty={(d) => {
                const all = [...(d ?? []), ...(domesticWorkers ?? [])];
                return all.length === 0;
              }}
              errorTitle="Couldn't load recurring passes"
              emptyMessage={t('resident.home.recurringEmpty')}
              loadingFallback={
                <div className="space-y-3 py-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="flex-1 space-y-1.5">
                        <Skeleton data-testid="skeleton" className="h-3.5 w-36" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </div>
                  ))}
                </div>
              }
            >
              {(passes) => {
                const all = [...(passes ?? []), ...(domesticWorkers ?? [])];
                return (
                  <>
                    {all.slice(0, 5).map((p) => (
                      <RecurringRow key={p.id} pass={p} />
                    ))}
                  </>
                );
              }}
            </QueryStateBoundary>
          </div>
        </div>
      </div>

      {/* Active codes — short-lived taxi + delivery codes the resident has issued */}
      {prop && <ActiveCodesPanel propertyId={prop.propertyId} />}

      {/* Inline create dialog for the delivery / taxi quick code (opens from the invite panel) */}
      {prop && dialogType !== null && (
        <PassCreateDialog
          open={true}
          onOpenChange={(open) => {
            if (!open) setDialogType(null);
          }}
          propertyId={prop.propertyId}
          passType={dialogType}
        />
      )}
    </div>
  );
}
